import * as p from '@clack/prompts'

import { hasActiveSpinner } from './spinner.js'

/**
 * Make Ctrl-C during setup print `Setup cancelled.` and exit 0 instead of
 * Node's default hard kill (or, worse, clack's spinner swallowing the signal
 * and letting the flow keep running half-cancelled).
 *
 * Signal coverage by state:
 * - Inside a clack prompt, stdin is in raw mode, so Ctrl-C never raises a
 *   signal — the prompt returns its cancel symbol and the existing
 *   `isCancel`/`onCancel` paths print the same message.
 * - While a spinner is animating, clack's spinner prints its `cancelMessage`
 *   line from a `process.on('exit')` hook, so this handler exits silently to
 *   avoid a duplicate line.
 * - Anywhere else (child processes with inherited stdio, gaps between
 *   steps), this handler prints the message itself.
 *
 * Returns a dispose function. Callers must dispose before handing signal
 * ownership to a long-lived child — the managed dev server forwards
 * SIGINT/SIGTERM to its child and must outlive the first Ctrl-C.
 */
export function installSetupCancelGuard(): () => void {
  const onSignal = () => {
    if (!hasActiveSpinner()) {
      p.cancel('Setup cancelled.')
    }
    process.exit(0)
  }
  process.on('SIGINT', onSignal)
  process.on('SIGTERM', onSignal)
  return () => {
    process.off('SIGINT', onSignal)
    process.off('SIGTERM', onSignal)
  }
}
